/**
 * Provides for handling whether an element is within the viewport. Also
 * lazy-loads a child images with `lazy-src` attribute.
 *
 * @summary In-viewport loading.
 */

/**
 * Transitioning state manager for InView.
 *
 * @class TransitionManager
 */
class TransitionManager {

	/**
	 * Creates an instance of TransitionManager.
	 *
	 * @param {HTMLElement} element The main transitioning element to watch for
	 *                              transitioning on.
	 */
	constructor( element ) {
		this.element = element;

		this._end = this._end.bind( this );
		element.addEventListener( 'transitionend', this._end );
	}

	/**
	 * Return the name of CSS Custom property for the delay value.
	 *
	 * This a pseudo class constant.
	 *
	 * @returns {string} The name of CSS Custom property for the delay value,
	 *                   including the -- prefix.
	 */
	get CSS_PROPERTY_NAME() {
		return '--inview-delay';
	}

	/**
	 * @summary Marks the end of the element's transition.
	 */
	_end() {
		this.element.style.removeProperty( this.CSS_PROPERTY_NAME );
		this.element.removeEventListener( 'transitionend', this._end );
	}

	/**
	 * @summary Marks element as loading (transitioning) in.
	 *
	 * @param {number} [index=0] The index of the element in the loading
	 *                           sequence for staggering effects.
	 */
	load( index = 0 ) {
		this.element.style.setProperty( this.CSS_PROPERTY_NAME, `${index * 100}ms` );
	}
}

export default class {

	/**
	 * Constructor.
	 *
	 * @param {HTMLElement[]} elements The elements to manage.
	 * @param {number} [threshold=0.2] A number between 0 and 1 as fraction of
	 *                                 the element's bounding box that should be
	 *                                 visible before it is considered in the
	 *                                 viewport.
	 */
	constructor( elements, threshold = 0.2 ) {
		this.threshold = threshold;

		const observer = window.IntersectionObserver ?
			new IntersectionObserver( this._onIntersect.bind( this ), { threshold }) :
			false;

		elements.forEach( ( element, index ) => {
			element.className += ` ${this.constructor.OUTSIDE_VIEWPORT_CLASSNAME}`;

			element.transitionManager = new TransitionManager( element );

			element._images = Array.from( element.querySelectorAll( 'img[lazy-src]' ) );
			if ( element._images.length ) {
				element._images.forEach( image => {
					image.classList.add( this.constructor.IMAGE_UNLOADED_CLASSNAME );
				});
			}

			if ( observer ) {
				observer.observe( element );
			} else {
				this.load( element, index );
			}
		});
	}

	/**
	 * @summary Not in-view CSS class name.
	 *
	 * Gets the CSS state class for a element that is not in-view.
	 *
	 * @returns {string} The CSS state class for a element that is not in-view.
	 */
	static get OUTSIDE_VIEWPORT_CLASSNAME() {
		return 'is-outside-viewport';
	}

	/**
	 * @summary Unloaded image CSS class name.
	 *
	 * Gets the CSS state class for a element's image that has not been loaded
	 * yet.
	 *
	 * @returns {string} The CSS state class for a element's image that has not
	 *                   been loaded yet.
	 */
	static get IMAGE_UNLOADED_CLASSNAME() {
		return 'is-image-unloaded';
	}

	/**
	 * @summary Manages an intersection change.
	 *
	 * @param {IntersectionObserverEntry[]} entries List of intersection entries.
	 * @param {IntersectionObserver} observer The observer managing the change.
	 */
	_onIntersect( entries, observer ) {
		entries
			.filter( entry => entry.isIntersecting || entry.intersectionRatio > this.threshold )
			.forEach( ( entry, i ) => {
				this.load( entry.target, i );

				observer.unobserve( entry.target );
			});
	}

	/**
	 * @summary Marks an element as in the viewport.
	 *
	 * @param {HTMLElement} element The element.
	 * @param {HTMLImageElement} element._image Optional. The element's
	 *                                          associated image (if it has one).
	 * @param {number} index The index of the current element in the loaded
	 *                       selection, for staggered transition effects.
	 */
	load( element, index ) {
		requestAnimationFrame( () => {

			// Add delays for staggering
			element.transitionManager.load( index );

			element.classList.remove( this.constructor.OUTSIDE_VIEWPORT_CLASSNAME );
		});

		if ( element._images.length ) {
			element._images.forEach( this._loadImage, this );
		}
	}

	/**
	 * @summary Loads element's image.
	 *
	 * Prefix `src` and `srcset` attributes of the image with `lazy-` to have
	 * the image available to be be lazy loaded.
	 *
	 * @param {HTMLImageElement} image The image element to load.
	 */
	_loadImage( image ) {

		// Remove image unloaded class when image is loaded
		image.addEventListener( 'load', () => {
			image.classList.remove( this.constructor.IMAGE_UNLOADED_CLASSNAME );
		});

		// Copy `lazy-` prefixed attributes without the suffix to load the image.
		Array.from( image.attributes )
			.filter( attribute => attribute.name.indexOf( 'lazy-' ) === 0 )
			.forEach( attribute => {
				image.setAttribute( attribute.name.replace( /^lazy-/, '' ), attribute.value );
			});
	}
}



// WEBPACK FOOTER //
// ./src/js/in-view.js